/**
 * betaService — wrappers client das callables do bloco operacional pré-beta.
 *
 * As 3 aqui são user-facing. As administrativas (setBetaGate, allowlist,
 * waitlist, listBetaFeedback) NÃO têm wrapper client de propósito: são gated
 * por custom claim `admin` e hoje são operadas pelo `scripts/beta-admin.cjs`.
 * Quando a BetaSection do AdminShell existir, ela adiciona os wrappers dela.
 */

import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";

const functionsRegion = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as string | undefined;

function getFns() {
  return functionsRegion?.trim()
    ? getFunctions(getApp(), functionsRegion.trim())
    : getFunctions(getApp());
}

export type FeedbackCategory = "bug" | "idea" | "other";

export interface SubmitFeedbackInput {
  category: FeedbackCategory;
  text: string;
  route: string;
  releaseId: string | null;
}

/**
 * Envia um relato de feedback. `uid` e `email` NÃO vão no payload — o servidor
 * os tira do token verificado. Teto de 5/dia server-side: ao estourar, a
 * callable devolve `resource-exhausted` com a copy pronta pra exibir.
 */
export async function submitFeedback(
  input: SubmitFeedbackInput,
): Promise<{ ok: true; id: string }> {
  const fn = httpsCallable<SubmitFeedbackInput, { ok: true; id: string }>(
    getFns(),
    "submitFeedback",
  );
  const result = await fn(input);
  return result.data;
}

export interface CheckBetaAccessResult {
  allowed: boolean;
  gateEnabled: boolean;
}

/**
 * Veredito do porteiro do beta. O e-mail vem do token no servidor — o client
 * não manda nem consegue forjar. Com o gate desligado devolve sempre
 * `{allowed: true, gateEnabled: false}`.
 */
export async function checkBetaAccess(): Promise<CheckBetaAccessResult> {
  const fn = httpsCallable<Record<string, never>, CheckBetaAccessResult>(
    getFns(),
    "checkBetaAccess",
  );
  const result = await fn({});
  return result.data;
}

export interface JoinWaitlistInput {
  /** @ do Instagram/TikTok — não vem no token do Google. */
  handle: string | null;
  /** Código de creator, se a pessoa tiver um. */
  code: string | null;
  /** Consentimento LGPD de receber e-mail sobre o beta. */
  consent: boolean;
  /** Campo livre legado da tela antiga. Hoje o formulário não pergunta. */
  reason?: string | null;
}

/**
 * Entra na lista de espera (idempotente por e-mail).
 *
 * O e-mail NÃO vai no payload — o servidor tira do token verificado, que é o
 * que impede alguém de reservar lugar com o endereço dos outros.
 */
export async function joinWaitlist(
  input: JoinWaitlistInput,
): Promise<{ ok: true; alreadyOnList: boolean }> {
  const fn = httpsCallable<JoinWaitlistInput, { ok: true; alreadyOnList: boolean }>(
    getFns(),
    "joinWaitlist",
  );
  const result = await fn({ reason: null, ...input });
  return result.data;
}
